// Emulating https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/core%400.8.0/packages/chakra-ui/src/Button/styles.js
import { borders, variantColorToShade } from "lib/theme";

const LG_HEIGHT = 14; // 56px
const MD_HEIGHT = 11; // 44px
const SM_HEIGHT = 8; // 32px

const baseProps = {
  border: "none",
  cursor: "pointer",
  fontWeight: "bold",
  overflow: "hidden",
  rounded: "full",
  textDecoration: "none",
  m: 0,
};

const sizes = {
  lg: {
    height: LG_HEIGHT,
    minWidth: 12,
    fontSize: "lg",
    px: 6,
    py: 2,
  },
  md: {
    height: MD_HEIGHT,
    minWidth: 10,
    fontSize: "lg",
    px: 5,
  },
  sm: {
    height: SM_HEIGHT,
    minWidth: 10,
    fontSize: "sm",
    px: 4,
  },
};

const sizeProps = ({ size }) => sizes[size];

const solidVariantProps = ({ variantColor }) => {
  return {
    bg: variantColorToShade(variantColor),
    _hover: {
      boxShadow: "inputUp",
    },
    _active: {
      boxShadow: "inputDown",
    },
    _focus: {
      boxShadow: "inputUp",
    },
    _before: {
      content: `""`,
      background:
        variantColor === "white"
          ? "linear-gradient(96.09deg, rgba(3, 3, 56, 1) 0%, rgba(3, 3, 56, 0) 97.87%)"
          : "linear-gradient(96.09deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 97.87%)",
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: "100%",
      pointerEvents: "none",
      transition: "0.15s ease-in-out opacity",
      opacity: 0.16,
    },
    _hoverBefore: {
      opacity: 0.24,
    },
    _activeBefore: {
      opacity: 0.08,
    },
    color: variantColor === "white" ? "navy.500" : "white",
    _disabled: {
      bg: "gray.500",
      cursor: "not-allowed",
      boxShadow: "none",
    },
  };
};

const outlineVariantProps = ({ variantColor }) => {
  return {
    color: variantColorToShade(variantColor),
    bg: "white.50",
    border: borders.thickness,
    borderColor: "current",
    _hover: {
      boxShadow: "inputUp",
    },
    _active: {
      boxShadow: "inputDown",
    },
    _focus: {
      boxShadow: "inputUp",
    },
    _disabled: {
      color: "gray.500",
      opacity: 1,
      cursor: "not-allowed",
      boxShadow: "none",
    },
  };
};

const ghostVariantProps = ({ variantColor }) => {
  if (variantColor === "whiteAlpha") {
    return {
      color: `${variantColor}.900`,
      bg: "transparent",
      _hover: {
        bg: `${variantColor}.200`,
      },
      _active: {
        bg: `${variantColor}.300`,
      },
      _focus: {
        bg: `${variantColor}.200`,
      },
    };
  }
  return {
    color: variantColorToShade(variantColor),
    bg: "transparent",
    _hover: {
      bg: `${variantColor}.50`,
    },
    _active: {
      bg: `${variantColor}.200`,
    },
    _focus: {
      bg: `${variantColor}.50`,
    },
  };
};

const unstyledStyleProps = {
  userSelect: "inherit",
  bg: "none",
  border: 0,
  color: "inherit",
  display: "inline",
  font: "inherit",
  lineHeight: "inherit",
  px: 0,
  py: 0,
  textAlign: "inherit",
  rounded: "none",
};

const variantProps = (props) => {
  switch (props.variant) {
    case "solid":
      return solidVariantProps(props);
    case "outline":
      return outlineVariantProps(props);
    case "ghost":
      return ghostVariantProps(props);
    case "unstyled":
      return unstyledStyleProps;
    default:
      return {};
  }
};

// Emulating https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/core%400.8.0/packages/chakra-ui/src/Button/styles.js#L211-L226
const buttonStyle = (props) => {
  return {
    ...baseProps,
    ...sizeProps(props),
    ...variantProps(props),
  };
};

export default buttonStyle;
